import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import PaginatedTable, {
  PaginatedTableColumn,
  SortConfig,
} from "../ui/paginated-table";
import { MoreHorizontalIcon, PlayIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

// Define the interface inline as per codebase patterns
interface ConversationTree {
  id: number;
  conversation_flow_name: string;
  conversation_flow_version: number;
  is_latest: boolean;
  created_at: string;
  application_name: string;
  intent_name?: string;
  conversation_flow_count?: number;
  status?: string;
}

interface ConversationTreesTableProps {
  conversationTrees: ConversationTree[];
  loading: boolean;
  sortConfig: SortConfig<ConversationTree>;
  onSort: (key: keyof ConversationTree) => void;
  onEdit: (tree: ConversationTree) => void;
  onDelete: (tree: ConversationTree) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
}

// Format status text: convert underscores to spaces, title case
const formatStatus = (status: string | undefined): string => {
  if (!status || status === "UNKNOWN") return "Unknown";

  // Convert underscores to spaces and title case each word
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Get status badge color based on status value
const getStatusColor = (status: string | undefined): string => {
  if (!status || status === "UNKNOWN") {
    return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  }

  const statusLower = status.toLowerCase();

  // Active/enabled/ready states - Green
  if (
    statusLower.includes("enabled") ||
    statusLower.includes("active") ||
    statusLower.includes("ready") ||
    statusLower.includes("completed")
  ) {
    return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
  }

  // Disabled/failed states - Red
  if (
    statusLower.includes("disabled") ||
    statusLower.includes("failed") ||
    statusLower.includes("error")
  ) {
    return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
  }

  // Pending/processing/running states - Yellow
  if (
    statusLower.includes("pending") ||
    statusLower.includes("processing") ||
    statusLower.includes("running") ||
    statusLower.includes("progress")
  ) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
  }

  // Default - Gray
  return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
};

const ConversationTreesTable: React.FC<ConversationTreesTableProps> = ({
  conversationTrees,
  loading,
  sortConfig,
  onSort,
  onEdit,
  onDelete,
  currentPage,
  setCurrentPage,
  pageSize,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: string | number) => {
    if (!timestamp) return "Never";
    const date = moment(timestamp);
    const now = moment();

    // For dates within the same year, display as three-letter month and date
    if (date.year() === now.year()) {
      return date.format("MMM D");
    }
    // For dates older than one year, display full MM/DD/YYYY format
    return date.format("MM/DD/YYYY");
  };

  const formatTime = (timestamp: string | number) => {
    if (!timestamp) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const renderActions = (tree: ConversationTree) => {
    return (
      <div className="flex items-center gap-2">
        {/* Execute Button */}
        <Button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onEdit(tree);
          }}
          className="h-8 px-3 bg-foreground hover:bg-foreground/90 text-background">
          <PlayIcon className="w-3 h-3 mr-2" />
          Execute
        </Button>

        {/* More Actions Menu */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Simple dropdown menu using native browser context menu
              const menu = document.createElement("div");
              menu.className =
                "absolute right-0 top-8 z-50 min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";
              menu.innerHTML = `
                <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-red-600">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                  Delete Tree
                </button>
              `;

              // Position and show menu
              const container = e.currentTarget.parentElement;
              if (container) {
                container.appendChild(menu);

                // Add click handlers
                const buttons = menu.querySelectorAll("button");
                buttons[0].onclick = (evt) => {
                  evt.stopPropagation();
                  onDelete(tree);
                  menu.remove();
                };

                // Remove menu when clicking outside
                const handleClickOutside = (evt: Event) => {
                  if (!menu.contains(evt.target as Node)) {
                    menu.remove();
                    document.removeEventListener("click", handleClickOutside);
                  }
                };
                setTimeout(
                  () => document.addEventListener("click", handleClickOutside),
                  0
                );
              }
            }}>
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  const columns: PaginatedTableColumn<ConversationTree>[] = [
    {
      key: "conversation_flow_name",
      header: "Name",
      width: "w-[30%]",
      sortable: true,
      render: (tree) => (
        <div className="space-y-1">
          <div className="font-gc-med text-foreground hover:text-maitai-green transition-colors">
            {tree.conversation_flow_name}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-muted text-foreground font-medium">
              v{tree.conversation_flow_version}
            </span>
            <span>•</span>
            <span>{tree.application_name}</span>
          </div>
        </div>
      ),
    },
    {
      key: "intent_name",
      header: "Intent",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div className="text-sm font-gc-reg text-foreground">
          {tree.intent_name || "dev1"}
        </div>
      ),
    },
    {
      key: "conversation_flow_count",
      header: "Paths",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div
          className={cn(
            "text-sm font-gc-reg",
            tree.conversation_flow_count && tree.conversation_flow_count > 0
              ? "text-foreground"
              : "text-muted-foreground"
          )}>
          {tree.conversation_flow_count?.toLocaleString() || "5"}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div
          className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            getStatusColor(tree.status)
          )}>
          {formatStatus(tree.status)}
        </div>
      ),
    },
    {
      key: "created_at",
      header: "Created",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(tree.created_at)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(tree.created_at)}
          </div>
        </div>
      ),
    },
    {
      key: "actions" as keyof ConversationTree,
      header: "",
      width: "w-[140px]",
      sortable: false,
      render: (tree) => renderActions(tree),
    },
  ];

  const defaultSort = (a: ConversationTree, b: ConversationTree) => {
    // Default sort by created_at descending
    const dateA = moment(a.created_at).valueOf();
    const dateB = moment(b.created_at).valueOf();
    return dateB - dateA;
  };

  return (
    <PaginatedTable
      data={conversationTrees}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      emptyTitle="No conversation trees found"
      emptyDescription="Create your first conversation tree to get started with building interactive flows."
      onRowClick={(tree) => navigate(`/forge/conversation-tree?id=${tree.id}`)}
      defaultSort={defaultSort}
    />
  );
};

export default ConversationTreesTable;
